import React, { useCallback, useEffect, useState } from 'react';
import {
    getSyncRecord,
    listSyncLogs,
    listSyncRecords,
    retrySyncImage,
    SyncLog,
    SyncRecord,
} from '../../api/datasync';
import { ApiError } from '../../api/request';
import { useCan } from '../../store';
import { formatTime } from '../../utils/time';
import { toast } from '../../utils/toast';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import { alertDialog } from '../../utils/dialog';

const PAGE_SIZE = 20;
const KIND_OPTS = [
    { value: 'incr', label: '增量' },
    { value: 'reconcile', label: '对账' },
    { value: 'manual', label: '手动' },
];
const STATUS_OPTS = [
    { value: 'ok', label: '成功' },
    { value: 'partial', label: '部分成功' },
    { value: 'failed', label: '失败' },
];

const KIND_LABEL: Record<string, string> = { incr: '增量', reconcile: '对账', manual: '手动' };
const LOG_STATUS_TAG: Record<string, string> = {
    ok: 'bg-emerald-50 text-emerald-700',
    partial: 'bg-amber-50 text-amber-700',
    failed: 'bg-red-50 text-red-700',
};
const ACTION_LABEL: Record<string, string> = { created: '新增', updated: '更新', deleted: '删除' };
const IMAGE_LABEL: Record<string, string> = {
    none: '-',
    done: '已搬图',
    pending: '待补图',
    failed: '搬图失败',
};

/** 批次行展开后的行级记录区. */
function RecordsPanel({ batchId }: { batchId: number }) {
    const canManage = useCan('datasync', 'update');
    const [list, setList] = useState<SyncRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState<SyncRecord | null>(null);

    const load = useCallback(async () => {
        try {
            const res = await listSyncRecords({ batchId, page, pageSize: PAGE_SIZE });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            toast(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        }
    }, [batchId, page]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (id: number) => {
        try {
            setDetail(await getSyncRecord(id));
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        }
    };

    const onRetry = async (id: number) => {
        try {
            const res = await retrySyncImage(id);
            toast(res.message || '已重试');
            load();
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        }
    };

    return (
        <div className="bg-slate-50 rounded p-3">
            <table className="w-full text-[13px]">
                <thead>
                    <tr>
                        <th className="th">行 id</th>
                        <th className="th">动作</th>
                        <th className="th">图片</th>
                        <th className="th">错误</th>
                        <th className="th">时间</th>
                        <th className="th">数据</th>
                    </tr>
                </thead>
                <tbody>
                    {list.map((r) => (
                        <tr key={r.id}>
                            <td className="td font-mono">{r.rowId}</td>
                            <td className="td">{ACTION_LABEL[r.action] || r.action}</td>
                            <td className="td">
                                {IMAGE_LABEL[r.imageStatus] || r.imageStatus}
                                {r.retryCount > 0 && ` (重试${r.retryCount})`}
                                {canManage && (r.imageStatus === 'pending' || r.imageStatus === 'failed') && (
                                    <button type="button" className="btn-ghost ml-1" onClick={() => onRetry(r.id)}>
                                        重试搬图
                                    </button>
                                )}
                            </td>
                            <td className="td max-w-[220px] truncate text-red-600" title={r.error}>
                                {r.error || '-'}
                            </td>
                            <td className="td">{formatTime(r.createdAt)}</td>
                            <td className="td">
                                <button type="button" className="btn-ghost" onClick={() => openDetail(r.id)}>
                                    源/落库对照
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!list.length && (
                        <tr>
                            <td className="td text-slate-400" colSpan={6}>
                                本批次无行级记录
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="flex justify-end">
                <Pagination
                    page={page}
                    totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                    total={total}
                    onChange={setPage}
                />
            </div>
            {detail && <RecordDetailModal record={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

/** 双份数据左右对照弹窗 (需求4: 记录同步的两份数据). */
function RecordDetailModal({ record, onClose }: { record: SyncRecord; onClose: () => void }) {
    const pretty = (raw?: string) => {
        if (!raw) return '(无)';
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw;
        }
    };
    return (
        <Modal
            open
            width={960}
            title={`${record.tableName} #${record.rowId} · ${ACTION_LABEL[record.action] || record.action}`}
            subtitle={`批次 #${record.batchId} · ${formatTime(record.createdAt)}`}
            onClose={onClose}
        >
            <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                    <div className="font-semibold text-slate-600 mb-1">美服源数据</div>
                    <pre className="bg-slate-50 rounded p-2 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
                        {pretty(record.sourceData)}
                    </pre>
                </div>
                <div>
                    <div className="font-semibold text-slate-600 mb-1">国服落库数据 (URL 已改写)</div>
                    <pre className="bg-slate-50 rounded p-2 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
                        {pretty(record.targetData)}
                    </pre>
                </div>
            </div>
        </Modal>
    );
}

function SyncLogList() {
    // 筛选: 输入框值与已提交值分离
    const [inputTable, setInputTable] = useState('');
    const [table, setTable] = useState('');
    const [kind, setKind] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [list, setList] = useState<SyncLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listSyncLogs({ table, kind, status, page, pageSize: PAGE_SIZE });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [table, kind, status, page]);

    useEffect(() => {
        load();
    }, [load]);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setTable(inputTable.trim());
    };

    return (
        <div className="space-y-4">
            <form onSubmit={onSearch} className="card p-3 flex items-center gap-2 text-sm flex-wrap">
                <input
                    className="inp w-48"
                    placeholder="表名, 如 material"
                    value={inputTable}
                    onChange={(e) => setInputTable(e.target.value)}
                />
                <SearchSelect className="w-32" value={kind} onChange={(v) => { setKind(v); setPage(1); }}
                    placeholder="全部类型" options={KIND_OPTS} clearable />
                <SearchSelect className="w-32" value={status} onChange={(v) => { setStatus(v); setPage(1); }}
                    placeholder="全部状态" options={STATUS_OPTS} clearable />
                <button type="submit" className="btn-ghost">
                    查询
                </button>
                {loading && <span className="text-slate-400">加载中...</span>}
            </form>
            {error && <div className="card p-3 text-sm text-red-700 bg-red-50">{error}</div>}

            <div className="card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="th">时间</th>
                            <th className="th">表</th>
                            <th className="th">类型</th>
                            <th className="th">+新增</th>
                            <th className="th">~更新</th>
                            <th className="th">-删除</th>
                            <th className="th">搬图</th>
                            <th className="th">耗时</th>
                            <th className="th">状态</th>
                            <th className="th">错误</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((lg) => (
                            <React.Fragment key={lg.id}>
                                <tr
                                    className="cursor-pointer hover:bg-slate-50"
                                    onClick={() => setExpanded(expanded === lg.id ? null : lg.id)}
                                    title="点击展开/收起行级记录"
                                >
                                    <td className="td whitespace-nowrap">{formatTime(lg.createdAt)}</td>
                                    <td className="td font-mono">{lg.tableName}</td>
                                    <td className="td">{KIND_LABEL[lg.kind] || lg.kind}</td>
                                    <td className="td text-emerald-700">{lg.createdCnt || '-'}</td>
                                    <td className="td">{lg.updatedCnt || '-'}</td>
                                    <td className="td text-red-700">{lg.deletedCnt || '-'}</td>
                                    <td className="td">{lg.imageCnt || '-'}</td>
                                    <td className="td">{lg.costMs}ms</td>
                                    <td className="td">
                                        <span className={`tag ${LOG_STATUS_TAG[lg.status] || ''}`}>{lg.status}</span>
                                    </td>
                                    <td className="td max-w-[240px] truncate text-red-600" title={lg.error}>
                                        {lg.error || '-'}
                                    </td>
                                </tr>
                                {expanded === lg.id && (
                                    <tr>
                                        <td className="td" colSpan={10}>
                                            <RecordsPanel batchId={lg.id} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {!loading && !list.length && (
                            <tr>
                                <td className="td text-slate-400" colSpan={10}>
                                    暂无同步记录 (空轮不落日志)
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <Pagination
                    page={page}
                    totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                    total={total}
                    onChange={setPage}
                />
            </div>
        </div>
    );
}

export default SyncLogList;
